/**
 * Panel de facturacion AFIP.
 *
 * Es el panel mas chico de la pantalla y no por falta de datos: la tabla
 * `invoices` tiene cientos de filas reales. Es porque el UNICO endpoint de
 * lectura que expone hoy el backend es
 * `GET /api/v1/invoicing/status/{store_id}`, que devuelve exactamente dos
 * numeros: facturas creadas hoy y en los ultimos 7 dias. No hay endpoint que
 * devuelva el total de la tienda ni el listado (ver `screens/invoicing/api.ts`,
 * que documenta el contrato faltante).
 *
 * Por eso los dos contadores se muestran con lo que miden escrito al lado. Un
 * "0" en "facturas hoy" NO significa que la tienda no tenga facturas, y un
 * panel que no aclarara esa diferencia estaria diciendo algo falso con datos
 * verdaderos.
 *
 * Este modulo no toca nada de AFIP: solo lee contadores.
 */

import type { InvoicingStatus } from "./api";
import { formatCount } from "./format";
import { Figure, Note, Panel } from "./ui";

export function InvoicingPanel({ status }: { readonly status: InvoicingStatus }) {
  const quiet = status.invoices_today === 0 && status.invoices_last_7_days === 0;

  return (
    <Panel
      title="Facturacion"
      to="/facturacion"
      toLabel="Ver facturas"
      lead="Comprobantes emitidos recientemente para esta tienda."
    >
      <div className="stat-grid">
        <Figure
          label="Facturas hoy"
          value={formatCount(status.invoices_today)}
          hint="Filas de invoices con created_at de hoy."
        />
        <Figure
          label="Ultimos 7 dias"
          value={formatCount(status.invoices_last_7_days)}
          hint="Ventana movil de 7 dias sobre created_at."
        />
      </div>

      {quiet ? (
        <Note tone="warn">
          Sin comprobantes en los ultimos 7 dias. Eso <strong>no</strong> quiere
          decir que la tienda no tenga facturas: estos son contadores de ventana
          y la tienda puede tener cientos de comprobantes anteriores.
        </Note>
      ) : null}

      <Note>
        Los dos numeros cuentan <code>created_at</code>, o sea cuando se creo la
        fila, no la fecha fiscal del comprobante. El total historico de la tienda
        no aparece aca porque ningun endpoint lo devuelve todavia: falta el
        listado de facturas, que es la unica deuda del backend que arrastra la
        pantalla de Facturacion.
      </Note>
    </Panel>
  );
}
